"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, Layers, Gauge, Sparkles, SlidersHorizontal as Sliders } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const hubCards = [{
  badge: "Process Analytical Technology",
  title: "PAT Solution Hub",
  description: "In-line and on-line optical spectroscopy systems designed for continuous manufacturing, real-time release testing, and regulated process control.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology_system.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology_system.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology_system.png-wvc-sizes",
    alt: "Process analytical technology monitoring unit"
  },
  features: ["Real-time blend uniformity & drying endpoint monitoring", "Fiber-optic multiplexed probe integration for harsh lines", "Compliant with 21 CFR Part 11 and cGMP data integrity", "Custom flow cells for liquids, slurries, and powders"],
  primaryLink: "/ProcessAnalyticalTechnologyPage",
  primaryLabel: "Explore PAT Solutions",
  secondaryLink: "/DiscussYourApplicationPage",
  secondaryLabel: "Discuss Application",
  icon: Activity
}, {
  badge: "Industrial Spectroscopy",
  title: "Bruker Process Spectrometers",
  description: "Rugged FT-NIR spectrometers and process sensors engineered for non-destructive solid and liquid testing directly inside industrial process lines.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_process_spectrometer_matrix.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_process_spectrometer_matrix.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_process_spectrometer_matrix.png-wvc-sizes",
    alt: "Bruker Matrix-F series process spectrometer unit"
  },
  features: ["MATRIX-F II multiplexed FT-NIR for up to 6 measurement points", "Bruker BEAM compact solid-state NIR for blenders & dryers", "ATEX and explosion-proof certified housing options", "Direct DCS and SCADA industrial communication protocols"],
  primaryLink: "/NirProcessSpectrometersPage",
  primaryLabel: "View Process Spectrometers",
  secondaryLink: "/MatrixFIIPage",
  secondaryLabel: "MATRIX-F II Details",
  icon: Cpu
}];
const highlights = [{
  icon: Gauge,
  title: "Real-Time Optimization",
  description: "Instantaneous batch-to-batch monitoring and closed-loop process feedback."
}, {
  icon: Layers,
  title: "Turnkey Integration",
  description: "Complete hardware mounting, optical fiber routing, and automation integration."
}, {
  icon: Sliders,
  title: "Chemometric Calibration",
  description: "Expert model development, transfer assistance, and method qualification support."
}];
export default function ProcessHub() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="processsolutionshub" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="page-container">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-medium uppercase tracking-wider">
              <Sparkles className="size-3 text-primary mr-1" />
              Process Solutions
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Process Analytical Technology & Spectrometers
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            Accelerate manufacturing cycles and maintain rigorous batch quality with dedicated process spectroscopy, non-invasive optical probes, and compliant analytics.
          </p>
        </div>

        {/* 2-Column Main Hub Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {hubCards.map((card, idx) => {
          const IconComponent = card.icon;
          return <motion.div key={card.title} variants={itemVariants} data-index={idx} className="h-full">
                <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 overflow-hidden">
                  <div>
                    {/* Visual Asset Container */}
                    <div className="relative aspect-16/9 w-full overflow-hidden bg-muted">
                      <img src={card.image.src} data-wvc-srcset={card.image.srcset} data-wvc-sizes={card.image.sizes} alt={card.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-background/90 text-foreground backdrop-blur-xs border border-border shadow-xs font-medium">
                          {card.badge}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="pt-6 pb-2">
                      <div className="flex items-center gap-2.5 mb-2">
                        <div className="flex size-9 items-center justify-center rounded-lg bg-secondary text-primary">
                          <IconComponent className="size-5" />
                        </div>
                        <CardTitle className="text-2xl font-default font-bold text-card-foreground">
                          {card.title}
                        </CardTitle>
                      </div>
                      <CardDescription className="text-muted-foreground text-base leading-relaxed">
                        {card.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-4 pb-6">
                      <div className="space-y-3">
                        {card.features.map((feature, featureIdx) => <div key={featureIdx} data-index={featureIdx} className="flex items-start gap-2.5 text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </div>)}
                      </div>
                    </CardContent>
                  </div>

                  <div>
                    <Separator className="my-0" />
                    <CardFooter className="pt-6 pb-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 justify-between bg-secondary/30">
                      <Button asChild variant="default" className="w-full sm:w-auto font-medium">
                        <Link to={card.primaryLink}>
                          <span>{card.primaryLabel}</span>
                          <ArrowRight className="size-4 ml-1" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" className="w-full sm:w-auto font-medium">
                        <Link to={card.secondaryLink}>
                          {card.secondaryLabel}
                        </Link>
                      </Button>
                    </CardFooter>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Feature Highlights Grid */}
        <div className="border border-border rounded-xl bg-secondary/40 p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {highlights.map((item, index) => {
            const HighlightIcon = item.icon;
            return <div key={item.title} data-index={index} className="flex items-start gap-4">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                    <HighlightIcon className="size-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold font-default text-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Bottom Consultation Link Strip */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 rounded-lg border border-border bg-card">
          <div className="text-center sm:text-left">
            <p className="text-sm font-semibold text-foreground">
              Need technical validation for your production facility?
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Our specialists provide application consultation, feasibility testing, and integration advice across Ireland &amp; the UK.
            </p>
          </div>
          <Button asChild variant="secondary" size="sm" className="whitespace-nowrap shrink-0">
            <Link to="/ApplicationConsultationPage">
              Request Application Consultation
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}